import type React from 'react'
import ReactMarkdown from 'react-markdown'
import styled from 'styled-components'
import { theme } from '../../theme/theme'
import { Modal } from './Modal'

const Header = styled.div`
  padding: ${theme.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h2`
  margin: 0;
  font-size: ${theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: ${theme.typography.xl};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.secondary};
  padding: ${theme.spacing.xs};
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Content = styled.div`
  padding: ${theme.spacing.lg};
`

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`

const ResultCard = styled.div<{ $status: 'success' | 'error' | 'timeout' }>`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.lg};
  background: ${({ theme }) => theme.colors.cardBackground};
`

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.md};
`

const AthleteName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  font-size: ${theme.typography.md};
  margin-right: ${theme.spacing.sm};
`

const StatusBadge = styled.span<{ $status: 'success' | 'error' | 'timeout' }>`
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  margin-left: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.xs};
  font-weight: 500;
  text-transform: uppercase;
  background-color: ${(props) => {
    switch (props.$status) {
      case 'success':
        return theme.colors.success
      case 'error':
        return theme.colors.error
      case 'timeout':
        return theme.colors.warning
      default:
        return theme.colors.surface
    }
  }};
  color: white;
`

const Duration = styled.span`
  color: ${theme.colors.secondary};
  font-size: ${theme.typography.sm};
`

const MarkdownContent = styled.div`
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.6;

  h1, h2, h3, h4, h5, h6 {
    margin-top: ${theme.spacing.lg};
    margin-bottom: ${theme.spacing.md};
    color: ${({ theme }) => theme.colors.text};
    font-weight: 600;
  }

  h1 { font-size: ${theme.typography.xl}; }
  h2 { font-size: ${theme.typography.lg}; }
  h3 { font-size: ${theme.typography.md}; font-weight: 500; }

  p {
    margin-bottom: ${theme.spacing.md};
  }

  ul, ol {
    margin-bottom: ${theme.spacing.md};
    padding-left: ${theme.spacing.lg};
  }

  li {
    margin-bottom: ${theme.spacing.xs};
  }

  code {
    background-color: ${theme.colors.surfaceHover};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: ${theme.typography.sm};
  }

  pre {
    background-color: ${theme.colors.surfaceHover};
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.md};
    overflow-x: auto;
    margin-bottom: ${theme.spacing.md};
  }

  pre code {
    background: none;
    padding: 0;
  }

  strong {
    font-weight: 600;
  }

  blockquote {
    border-left: 3px solid ${theme.colors.border};
    padding-left: ${theme.spacing.md};
    margin: ${theme.spacing.md} 0;
    color: ${theme.colors.secondary};
  }
`

const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  padding: ${theme.spacing.md};
  background-color: rgba(239, 68, 68, 0.1);
  border-radius: ${theme.borderRadius.md};
  font-family: 'Monaco', 'Courier New', monospace;
  font-size: ${theme.typography.sm};
`

const Footer = styled.div`
  padding: ${theme.spacing.lg};
  display: flex;
  justify-content: flex-end;
`

const Button = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.md};
  font-weight: 600;
  cursor: pointer;
  transition: ${theme.transitions.default};

  &:hover {
    opacity: 0.9;
  }
`

export interface TaskExecutionResult {
  task_id: string
  user_id: string
  athlete_id?: string | null
  athlete_name?: string | null
  execution_duration_ms?: number
  response_text?: string
  error_message?: string
  status: 'success' | 'error' | 'timeout'
}

interface TaskExecutionResultsModalProps {
  isOpen: boolean
  onClose: () => void
  results: TaskExecutionResult[] | null
}

export const TaskExecutionResultsModal: React.FC<TaskExecutionResultsModalProps> = ({ isOpen, onClose, results }) => {
  if (!results) {
    return null
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="1000px"
      header={
        <Header>
          <Title>Analysis Results</Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </Header>
      }
      footer={
        <Footer>
          <Button onClick={onClose}>Close</Button>
        </Footer>
      }
    >
      <Content>
        <ResultsContainer>
          {results.map((result, index) => (
            <ResultCard key={`${result.task_id}-${index}`} $status={result.status}>
              <ResultHeader>
                <AthleteName>{result.athlete_name ?? 'Unknown Athlete'}</AthleteName>
                <div style={{ flexGrow: 1 }} />
                {result.execution_duration_ms && (
                  <Duration>{(result.execution_duration_ms / 1000).toFixed(0)}s</Duration>
                )}
                <StatusBadge $status={result.status}>{result.status}</StatusBadge>
              </ResultHeader>

              {result.status === 'success' && result.response_text && (
                <MarkdownContent>
                  <ReactMarkdown>{result.response_text}</ReactMarkdown>
                </MarkdownContent>
              )}

              {result.status === 'error' && result.error_message && <ErrorMessage>{result.error_message}</ErrorMessage>}

              {result.status === 'timeout' && <ErrorMessage>The task execution timed out.</ErrorMessage>}
            </ResultCard>
          ))}
        </ResultsContainer>
      </Content>
    </Modal>
  )
}
